/*
 * ManageDB — Chroma collection management UI.
 *
 * Talks directly to the Chroma v2 HTTP API (no chromadb npm dep) so the
 * same component works against:
 *   · http://127.0.0.1:8000          (local dev)
 *   · http://92.247.133.89:63140     (remote, HTTP — only from HTTP pages)
 *   · https://passer.scpdx.net/chroma (HTTPS proxy from prod)
 *
 * Features:
 *   · Search across all collections (free-text + name filter on table)
 *   · Sort by name (alpha), chunk count, or last-ingest date
 *   · Real chunk count + real last-ingested date (read from chunk metadata)
 *   · Inspect: per-section histogram, per-source histogram, sample chunks
 *   · Rename collection (Chroma v2 PUT /collections/{id} with new_name)
 *   · Delete collection (with confirm + summary of what'll be lost)
 *   · Create collection (basic; rarely needed since ingestion creates them)
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tag } from 'primereact/tag';
import { Dropdown } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import { Divider } from 'primereact/divider';
import './manageDB.css';

const TENANT = 'default_tenant';
const DATABASE = 'default_database';

// --------- low-level Chroma v2 API helpers (axios-based) ---------------
const apiBase = (url) =>
    `${(url || '').replace(/\/$/, '')}/api/v2/tenants/${TENANT}/databases/${DATABASE}`;

const chromaListCollections = (url) =>
    axios.get(`${apiBase(url)}/collections`, { timeout: 15000 })
         .then(r => r.data || []);

// Chroma v2 `count` response shape varies across versions and through
// reverse proxies. Possible shapes seen in the wild:
//   200 OK  body: 1234              (number)
//   200 OK  body: "1234"            (number-as-string)
//   200 OK  body: {"count": 1234}   (object wrap)
//   200 OK  body: {"total": 1234}   (alt key)
// We accept all of them; anything else → null (treated as "unknown",
// distinct from "0 chunks"). Old Chroma 0.4.x needs POST instead of GET.
function parseCount(raw) {
    if (raw == null) return null;
    if (typeof raw === 'number')
        return Number.isFinite(raw) ? raw : null;
    if (typeof raw === 'string') {
        const n = parseInt(raw.trim(), 10);
        return Number.isFinite(n) ? n : null;
    }
    if (typeof raw === 'object') {
        if (typeof raw.count === 'number') return raw.count;
        if (typeof raw.total === 'number') return raw.total;
    }
    return null;
}

async function chromaCount(url, cid) {
    const u = `${apiBase(url)}/collections/${cid}/count`;
    // Try GET first.
    try {
        const r = await axios.get(u, { timeout: 15000 });
        const n = parseCount(r.data);
        if (n != null) return n;
        // 200 OK but body is unparseable — fall through and try POST.
    } catch (_) {
        // 4xx / 5xx / network error — fall through and try POST.
    }
    // POST fallback (legacy Chroma versions accepted POST).
    try {
        const r = await axios.post(u, {}, { timeout: 15000 });
        const n = parseCount(r.data);
        return n; // may still be null — caller treats null as "unknown"
    } catch (e) {
        // Log so the user has a way to find which collection broke and why.
        console.warn(`[manageDB] count(${cid.slice(0, 8)}) GET+POST failed:`,
                     e?.message || e);
        return null;
    }
}

const chromaGet = (url, cid, body) =>
    axios.post(`${apiBase(url)}/collections/${cid}/get`, body, { timeout: 30000 })
         .then(r => r.data || {});

// Known embedding-model → vector dimension. Used by the Inspect dialog
// to flag mismatch warnings when a collection's recorded `embed_model`
// metadata disagrees with the dimension actually stored. Add entries
// here as new embedders enter the rotation; missing entries just skip
// the cross-check silently.
const DIM_SANITY = {
    'mxbai-embed-large':           1024,
    'mxbai-embed-large:latest':    1024,
    'nomic-embed-text':             768,
    'nomic-embed-text:latest':      768,
    'all-minilm':                   384,
    'all-minilm:latest':            384,
    'snowflake-arctic-embed':      1024,
    'bge-large':                   1024,
    'bge-m3':                      1024,
    // chat models used as embedders (Ollama returns hidden state):
    'mistral:latest':              4096,
    'mistral:7b-instruct':         4096,
    'llama3.1:8b':                 4096,
    'llama3.1:70b':                8192,
};

const chromaCreate = (url, name) =>
    axios.post(`${apiBase(url)}/collections`, { name }, { timeout: 15000 })
         .then(r => r.data);

const chromaRename = (url, cid, newName) =>
    axios.put(`${apiBase(url)}/collections/${cid}`,
              { new_name: newName }, { timeout: 15000 });

// Chroma v2 inconsistency: most endpoints take the collection ID,
// but DELETE takes the NAME (per the v2 OpenAPI spec). Send name
// first; if a server build accepts only id, fall back to that.
async function chromaDelete(url, cid, name) {
    const encodedName = encodeURIComponent(name);
    try {
        return await axios.delete(
            `${apiBase(url)}/collections/${encodedName}`,
            { timeout: 15000 });
    } catch (e) {
        const code = e?.response?.status;
        if (code === 404 || code === 405) {
            return await axios.delete(
                `${apiBase(url)}/collections/${cid}`,
                { timeout: 15000 });
        }
        throw e;
    }
}


// Pretty-print an ISO timestamp; falls back to a long-dash if blank.
function fmtDate(iso) {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return iso;
        return d.toLocaleString(undefined, {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit',
        });
    } catch {
        return iso;
    }
}

function fmtCount(n) {
    if (n == null) return '—';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
    return String(n);
}


const ManageDB = () => {
    const toast = useRef(null);

    const [chromaUrl, setChromaUrl] = useState(
        localStorage.getItem('selectedChromaDB') || 'http://127.0.0.1:8000'
    );
    const [rows, setRows] = useState([]);          // [{id, name, count, lastIngest, status}]
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [sortKey, setSortKey] = useState('name'); // 'name' | 'count' | 'lastIngest'
    const [sortDir, setSortDir] = useState('asc');  // 'asc' | 'desc'

    // Modal state
    const [renameOpen, setRenameOpen] = useState(false);
    const [renameTarget, setRenameTarget] = useState(null);
    const [renameNew, setRenameNew] = useState('');

    const [createOpen, setCreateOpen] = useState(false);
    const [createName, setCreateName] = useState('');

    const [inspectOpen, setInspectOpen] = useState(false);
    const [inspectTarget, setInspectTarget] = useState(null);
    const [inspectData, setInspectData] = useState(null);
    const [inspectLoading, setInspectLoading] = useState(false);

    // ---------- fetch + enrich ----------
    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const list = await chromaListCollections(chromaUrl);
            // Enrich each row with the actual chunk count + the
            // earliest `ingest_run` we find. Run concurrently.
            const enriched = await Promise.all(list.map(async (c) => {
                const cid  = c.id;
                const name = c.name;
                let count = null;
                let lastIngest = null;
                // Count + first-chunk fetches run in parallel. We always
                // try the first-chunk fetch, even when count failed —
                // if the collection has chunks, this populates the date
                // and gives the FE a fallback signal of "active".
                const countP = chromaCount(chromaUrl, cid)
                    .catch(() => null);
                const firstP = chromaGet(chromaUrl, cid,
                    { limit: 1, include: ['metadatas'] })
                    .catch(() => null);
                count = await countP;
                const sample = await firstP;
                const m = ((sample?.metadatas || [])[0]) || {};
                // Try every field name we know various ingest scripts
                // have used. newRAG pipeline uses `ingest_run`. Older
                // dbFromText / dbFromPDF / dbFromWEB used none — collections
                // built by them legitimately have no date and will fall
                // through to the collection-level metadata check below.
                lastIngest = m.ingest_run
                          || m.created_at
                          || m.createdAt
                          || m.creation_time
                          || m.timestamp
                          || m.inserted_at
                          || m.date
                          || m.time
                          || null;
                // Chroma's collection-level metadata (passed to
                // createCollection) sometimes carries a `created`
                // or `created_at`. Use it as a final fallback so
                // even legacy collections that were tagged at
                // creation time have *something* to sort by.
                if (!lastIngest && c.metadata) {
                    lastIngest = c.metadata.created_at
                              || c.metadata.created
                              || c.metadata.ingest_run
                              || null;
                }
                // If count failed but the first-chunk fetch returned an id,
                // we know it's not empty even though we don't have the exact
                // number; mark count as -1 (unknown) so the UI distinguishes
                // it from a real 0.
                const hasContent = (sample?.ids && sample.ids.length > 0);
                let status;
                if (count == null && hasContent) {
                    count = -1;            // sentinel: chunks exist but count unknown
                    status = 'active';
                } else if (count == null) {
                    status = 'unknown';
                } else if (count === 0) {
                    status = 'empty';
                } else {
                    status = 'active';
                }
                // dateState lets the UI distinguish:
                //   'present'  → real timestamp we can show + sort
                //   'undated'  → fetched fine, but the source format
                //                doesn't carry a date (legacy collections
                //                from dbFromPDF / dbFromText / dbFromWEB)
                //   'unknown'  → we couldn't fetch chunks at all
                let dateState;
                if (lastIngest)             dateState = 'present';
                else if (sample)            dateState = 'undated';
                else                        dateState = 'unknown';
                // Embedding model is stamped onto the collection-level
                // metadata at ingest time (chain_bridge / ingest_corpus's
                // get_collection_for_embed). Surface it so the inspect
                // dialog can show which model produced the vectors —
                // critical for diagnosing the dimension-mismatch 400s.
                const embedModel = (c.metadata && (c.metadata.embed_model
                                                   || c.metadata.embedding_model))
                                   || null;
                return {
                    id: cid,
                    name,
                    count,
                    lastIngest,
                    dateState,
                    status,
                    embedModel,
                };
            }));
            setRows(enriched);
            toast.current?.show({ severity: 'success', summary: 'Loaded',
                                   detail: `${enriched.length} collections`,
                                   life: 1800 });
        } catch (e) {
            console.error(e);
            toast.current?.show({ severity: 'error', summary: 'Load failed',
                                   detail: e?.message || String(e), life: 6000 });
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [chromaUrl]);

    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [reload]);

    // ---------- filter + sort ----------
    const filteredSorted = useMemo(() => {
        const q = search.trim().toLowerCase();
        let out = rows;
        if (q) {
            out = rows.filter(r =>
                (r.name || '').toLowerCase().includes(q) ||
                (r.id   || '').toLowerCase().includes(q)
            );
        }
        // For numeric / date sorts we treat null / -1 / empty as "below
        // everything else" and float them to the end regardless of
        // direction, so the user always sees the meaningful sorted
        // values first (an asc-by-date sort with 30 nulls at the top
        // would be useless).
        const isNullish = (v) =>
            v === null || v === undefined || v === '' || v === -1;
        out = [...out].sort((a, b) => {
            let av, bv;
            switch (sortKey) {
                case 'count':
                    av = a.count; bv = b.count; break;
                case 'lastIngest':
                    av = a.lastIngest; bv = b.lastIngest; break;
                case 'name':
                default:
                    av = (a.name || '').toLowerCase();
                    bv = (b.name || '').toLowerCase();
            }
            // Push nullish values to the bottom regardless of asc/desc.
            const aN = isNullish(av), bN = isNullish(bv);
            if (aN && bN) return 0;
            if (aN) return  1;
            if (bN) return -1;
            if (av < bv) return sortDir === 'asc' ? -1 : 1;
            if (av > bv) return sortDir === 'asc' ?  1 : -1;
            return 0;
        });
        return out;
    }, [rows, search, sortKey, sortDir]);

    // ---------- inspect ----------
    const openInspect = async (row) => {
        setInspectTarget(row);
        setInspectData(null);
        setInspectOpen(true);
        setInspectLoading(true);
        try {
            // Vector dimension probe: pull ONE chunk WITH embeddings
            // so we can read its length. Tells you immediately whether
            // a query embedded with model X (e.g. mxbai = 1024) will
            // hit this collection (e.g. built with mistral = 4096) —
            // dimension mismatch is the most common cause of /query
            // returning HTTP 400.
            let vectorDim = null;
            try {
                const probe = await chromaGet(chromaUrl, row.id, {
                    limit: 1,
                    include: ['embeddings'],
                });
                const emb = (probe?.embeddings || [])[0];
                if (Array.isArray(emb)) vectorDim = emb.length;
            } catch {
                /* swallow — dimension stays null, shown as "unknown" */
            }
            // Pull up to 2000 metadatas to build histograms. Larger
            // collections get capped — the user can see the cap warning.
            const PAGE = 1000;
            const MAX  = 2000;
            const sections = {};
            const sources  = {};
            const ingestRuns = new Set();
            const sampleChunks = [];
            let scanned = 0;
            for (let offset = 0; offset < (row.count || 0) && scanned < MAX; offset += PAGE) {
                const j = await chromaGet(chromaUrl, row.id, {
                    limit: Math.min(PAGE, MAX - scanned),
                    offset,
                    include: ['metadatas'],
                });
                const mds = j.metadatas || [];
                const ids = j.ids || [];
                for (let i = 0; i < mds.length; i++) {
                    const m = mds[i] || {};
                    sections[m.section || '(none)']
                        = (sections[m.section || '(none)'] || 0) + 1;
                    const src = m.source || m.filename || m.pdf || '(unknown)';
                    sources[src] = (sources[src] || 0) + 1;
                    if (m.ingest_run) ingestRuns.add(m.ingest_run);
                    if (sampleChunks.length < 5) {
                        sampleChunks.push({ id: ids[i] || '?', section: m.section,
                                            source: src });
                    }
                }
                scanned += mds.length;
                if (mds.length < PAGE) break;
            }
            const histTop = (h, n) => Object.entries(h)
                .sort((a, b) => b[1] - a[1])
                .slice(0, n);
            setInspectData({
                sectionsTop: histTop(sections, 10),
                sourcesTop:  histTop(sources,  10),
                ingestRuns:  [...ingestRuns].sort(),
                sampleChunks,
                scanned,
                capped: scanned < (row.count || 0),
                vectorDim,
            });
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: 'Inspect failed',
                                   detail: e?.message || String(e), life: 6000 });
        } finally {
            setInspectLoading(false);
        }
    };

    // ---------- rename ----------
    const openRename = (row) => {
        setRenameTarget(row);
        setRenameNew(row.name);
        setRenameOpen(true);
    };
    const submitRename = async () => {
        const nv = renameNew.trim();
        if (!nv || nv === renameTarget.name) { setRenameOpen(false); return; }
        try {
            await chromaRename(chromaUrl, renameTarget.id, nv);
            toast.current?.show({ severity: 'success', summary: 'Renamed',
                                   detail: `${renameTarget.name} → ${nv}`,
                                   life: 2500 });
            setRenameOpen(false);
            reload();
        } catch (e) {
            const msg = e?.response?.data?.error || e?.message || String(e);
            toast.current?.show({ severity: 'error', summary: 'Rename failed',
                                   detail: msg.includes('UNIQUE')
                                       ? `A collection named "${nv}" already exists.`
                                       : msg,
                                   life: 7000 });
        }
    };

    // ---------- delete ----------
    const askDelete = (row) => {
        confirmDialog({
            message: (
                <div>
                    <p style={{ margin: 0 }}>
                        Delete the collection&nbsp;
                        <strong>{row.name}</strong>?
                    </p>
                    {row.count > 0 && (
                        <p style={{ margin: '8px 0 0', color: '#b91c1c' }}>
                            ⚠ {fmtCount(row.count)} chunks will be permanently lost.
                        </p>
                    )}
                    <p style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
                        id: <code>{row.id}</code>
                    </p>
                </div>
            ),
            header: 'Confirm deletion',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Delete',
            rejectLabel: 'Cancel',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await chromaDelete(chromaUrl, row.id, row.name);
                    toast.current?.show({ severity: 'success', summary: 'Deleted',
                                           detail: row.name, life: 2500 });
                    reload();
                } catch (e) {
                    const body = e?.response?.data;
                    const msg = (typeof body === 'string' ? body : body?.error)
                                || e?.message || String(e);
                    toast.current?.show({ severity: 'error', summary: 'Delete failed',
                                           detail: msg, life: 7000 });
                }
            },
        });
    };

    // ---------- create ----------
    const submitCreate = async () => {
        const nv = createName.trim();
        if (!nv) return;
        try {
            await chromaCreate(chromaUrl, nv);
            toast.current?.show({ severity: 'success', summary: 'Created',
                                   detail: nv, life: 2500 });
            setCreateOpen(false);
            setCreateName('');
            reload();
        } catch (e) {
            const msg = e?.response?.data?.error || e?.message || String(e);
            toast.current?.show({ severity: 'error', summary: 'Create failed',
                                   detail: msg, life: 6000 });
        }
    };

    // ---------- column templates ----------
    const countTemplate = (row) => {
        const c = row.count;
        if (c == null) return (
            <span title="Count endpoint failed (404 / parse error / timeout) — collection state is unknown"
                  style={{ color: '#9ca3af' }}>?</span>
        );
        if (c === -1) return (
            <span title="Collection has chunks (first-chunk fetch succeeded) but the /count endpoint did not return a parseable number"
                  style={{ color: '#7c3aed', fontFamily: 'ui-monospace, Menlo, monospace',
                           fontWeight: 600 }}>≥ 1</span>
        );
        let color = '#6b7280';
        if (c > 0)     color = '#1d4ed8';
        if (c > 1000)  color = '#15803d';
        if (c > 10000) color = '#854d0e';
        return (
            <span style={{ fontFamily: 'ui-monospace, Menlo, monospace',
                           fontWeight: 600, color }}>
                {c.toLocaleString()}
            </span>
        );
    };

    const embedModelTemplate = (row) => {
        if (!row.embedModel) return (
            <span style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: 12 }}
                  title="Collection-level metadata doesn't carry an embed_model field. Open Inspect to see the actual vector dimension.">
                —
            </span>
        );
        return (
            <span style={{ fontFamily: 'ui-monospace, Menlo, monospace',
                           fontSize: 12, color: '#1d4ed8' }}
                  title={`Embed model recorded in this collection's Chroma metadata. Dimension: ${DIM_SANITY[row.embedModel] || '?'}.`}>
                {row.embedModel}
            </span>
        );
    };

    const dateTemplate = (row) => {
        if (row.lastIngest) return (
            <span style={{ fontFamily: 'ui-monospace, Menlo, monospace',
                           fontSize: 12, color: '#374151' }}>
                {fmtDate(row.lastIngest)}
            </span>
        );
        if (row.dateState === 'undated') return (
            <span title="Collection was built with an older vectoriser that didn't store a creation timestamp in chunk metadata"
                  style={{ fontFamily: 'ui-monospace, Menlo, monospace',
                           fontSize: 11, color: '#9ca3af', fontStyle: 'italic' }}>
                (undated — legacy)
            </span>
        );
        return (
            <span title="Couldn't fetch any chunks from this collection"
                  style={{ color: '#9ca3af' }}>?</span>
        );
    };

    const idTemplate = (row) => (
        <span title={row.id}
              style={{ fontFamily: 'ui-monospace, Menlo, monospace',
                       fontSize: 11, color: '#9ca3af' }}>
            {(row.id || '').slice(0, 8)}…
        </span>
    );

    const statusTemplate = (row) => {
        const severity =
            row.status === 'active'  ? 'success' :
            row.status === 'empty'   ? 'warning' :
                                       'danger';   // 'unknown'
        return <Tag value={row.status}
                    severity={severity}
                    style={{ fontWeight: 600 }} />;
    };

    const actionsTemplate = (row) => (
        <div className="manage-db-action-buttons">
            <Button icon="pi pi-search-plus"
                    rounded text severity="info"
                    tooltip="Inspect — chunk count, sections, sources, samples"
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => openInspect(row)} />
            <Button icon="pi pi-pencil"
                    rounded text severity="warning"
                    tooltip="Rename collection"
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => openRename(row)} />
            <Button icon="pi pi-trash"
                    rounded text severity="danger"
                    tooltip="Delete collection"
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => askDelete(row)} />
        </div>
    );

    // Summary numbers shown above the table. We sum only POSITIVE
    // counts; "-1" (unknown) and null don't contribute to the total.
    const totalCount  = rows.reduce(
        (acc, r) => acc + (r.count > 0 ? r.count : 0), 0);
    const activeCount  = rows.filter(r => r.status === 'active').length;
    const emptyCount   = rows.filter(r => r.status === 'empty').length;
    const unknownCount = rows.filter(r => r.status === 'unknown').length;

    return (
        <div className="manage-db-container">
            <Toast ref={toast} />
            <ConfirmDialog />
            <Tooltip target=".cmeta-pill" position="top" />

            {/* Header */}
            <div className="manage-db-hdr">
                <div className="manage-db-hdr-title">
                    <i className="pi pi-database" />
                    Chroma Collection Manager
                </div>
                <div className="manage-db-hdr-actions">
                    <Button icon="pi pi-refresh" label="Refresh"
                            outlined size="small"
                            loading={loading}
                            onClick={reload} />
                    <Button icon="pi pi-plus" label="New collection"
                            severity="success" size="small"
                            onClick={() => { setCreateName(''); setCreateOpen(true); }} />
                </div>
            </div>

            {/* Endpoint + summary */}
            <div className="manage-db-config">
                <div className="manage-db-config-row">
                    <label className="manage-db-config-label">
                        <i className="pi pi-server" />&nbsp;Chroma endpoint:
                    </label>
                    <InputText value={chromaUrl}
                               onChange={(e) => {
                                   setChromaUrl(e.target.value);
                                   localStorage.setItem('selectedChromaDB', e.target.value);
                               }}
                               className="manage-db-input"
                               placeholder="http://127.0.0.1:8000 or https://passer.scpdx.net/chroma" />
                </div>
                <div className="manage-db-stats">
                    <span className="cmeta-pill" data-pr-tooltip="Total collections found">
                        <i className="pi pi-list" />&nbsp;{rows.length} collections
                    </span>
                    <span className="cmeta-pill ok" data-pr-tooltip="Collections with at least 1 chunk">
                        <i className="pi pi-check-circle" />&nbsp;{activeCount} active
                    </span>
                    <span className="cmeta-pill warn" data-pr-tooltip="Collections with 0 chunks">
                        <i className="pi pi-exclamation-circle" />&nbsp;{emptyCount} empty
                    </span>
                    {unknownCount > 0 && (
                        <span className="cmeta-pill danger" data-pr-tooltip="Collections whose /count endpoint did not return a parseable number">
                            <i className="pi pi-question-circle" />&nbsp;{unknownCount} unknown
                        </span>
                    )}
                    <span className="cmeta-pill info" data-pr-tooltip="Sum of all positive chunk counts across all collections">
                        <i className="pi pi-box" />&nbsp;{totalCount.toLocaleString()} total chunks
                    </span>
                </div>
            </div>

            {/* Search + sort */}
            <div className="manage-db-toolbar">
                <span className="p-input-icon-left manage-db-search">
                    <i className="pi pi-search" />
                    <InputText value={search}
                               onChange={(e) => setSearch(e.target.value)}
                               placeholder="Search by name or id…" />
                </span>
                <Dropdown value={sortKey}
                          onChange={(e) => setSortKey(e.value)}
                          options={[
                              { label: 'Sort by name',  value: 'name' },
                              { label: 'Sort by chunk count', value: 'count' },
                              { label: 'Sort by last ingest date', value: 'lastIngest' },
                          ]}
                          className="manage-db-sort" />
                <Button icon={sortDir === 'asc' ? 'pi pi-sort-amount-up' : 'pi pi-sort-amount-down'}
                        outlined size="small"
                        tooltip={sortDir === 'asc' ? 'Ascending — click to reverse' : 'Descending — click to reverse'}
                        tooltipOptions={{ position: 'top' }}
                        onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')} />
            </div>

            {/* Table */}
            <div className="manage-db-table">
                {loading ? (
                    <div className="manage-db-loading">
                        <ProgressSpinner style={{ width: 50, height: 50 }} />
                        <p>Loading collections + chunk counts…</p>
                    </div>
                ) : (
                    <DataTable value={filteredSorted}
                               dataKey="id"
                               size="small"
                               showGridlines stripedRows
                               scrollable scrollHeight="60vh"
                               emptyMessage={
                                   search
                                       ? `No collections match "${search}".`
                                       : 'No collections found. Hit Refresh, or create one.'
                               }>
                        <Column field="name" header="Name"
                                body={(row) => (
                                    <span style={{ fontWeight: 600, color: '#111827' }}>
                                        {row.name}
                                    </span>
                                )}
                                style={{ minWidth: 220 }} />
                        <Column field="count" header="Chunks"
                                body={countTemplate}
                                style={{ width: 110, textAlign: 'right' }} />
                        <Column field="lastIngest" header="Last ingested"
                                body={dateTemplate}
                                style={{ width: 170 }} />
                        <Column field="status" header="Status"
                                body={statusTemplate}
                                style={{ width: 100 }} />
                        <Column field="embedModel" header="Embed model"
                                body={embedModelTemplate}
                                style={{ width: 170 }} />
                        <Column field="id" header="id"
                                body={idTemplate}
                                style={{ width: 110 }} />
                        <Column header="Actions" body={actionsTemplate}
                                style={{ width: 170, textAlign: 'center' }} />
                    </DataTable>
                )}
            </div>

            {/* Rename dialog */}
            <Dialog header={<><i className="pi pi-pencil" />&nbsp;Rename collection</>}
                    visible={renameOpen}
                    style={{ width: 500 }}
                    modal
                    onHide={() => setRenameOpen(false)}
                    footer={
                        <>
                            <Button label="Cancel" outlined
                                    onClick={() => setRenameOpen(false)} />
                            <Button label="Rename" icon="pi pi-check"
                                    severity="warning"
                                    disabled={!renameNew.trim() ||
                                              renameNew.trim() === renameTarget?.name}
                                    onClick={submitRename} />
                        </>
                    }>
                <div className="manage-db-dialog-content">
                    <p style={{ marginTop: 0, color: '#374151' }}>
                        Renaming&nbsp;
                        <code style={{ background: '#f3f4f6', padding: '2px 6px',
                                       borderRadius: 4 }}>
                            {renameTarget?.name}
                        </code>
                        &nbsp;(<code>{(renameTarget?.id || '').slice(0, 12)}…</code>)
                    </p>
                    <label className="manage-db-dialog-label">New name:</label>
                    <InputText value={renameNew}
                               onChange={(e) => setRenameNew(e.target.value)}
                               className="manage-db-dialog-input"
                               autoFocus
                               onKeyDown={(e) => { if (e.key === 'Enter') submitRename(); }} />
                    <p style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
                        Renaming is purely cosmetic; chunk IDs and references in
                        other CSVs / experiments stay unchanged.
                    </p>
                </div>
            </Dialog>

            {/* Create dialog */}
            <Dialog header={<><i className="pi pi-plus" />&nbsp;Create new collection</>}
                    visible={createOpen}
                    style={{ width: 500 }}
                    modal
                    onHide={() => setCreateOpen(false)}
                    footer={
                        <>
                            <Button label="Cancel" outlined
                                    onClick={() => setCreateOpen(false)} />
                            <Button label="Create" icon="pi pi-check"
                                    severity="success"
                                    disabled={!createName.trim()}
                                    onClick={submitCreate} />
                        </>
                    }>
                <div className="manage-db-dialog-content">
                    <label className="manage-db-dialog-label">Collection name:</label>
                    <InputText value={createName}
                               onChange={(e) => setCreateName(e.target.value)}
                               placeholder="e.g. corpus_v2"
                               className="manage-db-dialog-input"
                               autoFocus
                               onKeyDown={(e) => { if (e.key === 'Enter') submitCreate(); }} />
                    <p style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
                        Usually collections are created by the ingestion pipeline,
                        not by hand. This is here for the occasional empty
                        placeholder you want to set up in advance.
                    </p>
                </div>
            </Dialog>

            {/* Inspect dialog */}
            <Dialog header={
                        <>
                            <i className="pi pi-search-plus" />&nbsp;Inspect&nbsp;
                            <span style={{ fontWeight: 600 }}>{inspectTarget?.name}</span>
                        </>
                    }
                    visible={inspectOpen}
                    style={{ width: 720, maxHeight: '90vh' }}
                    modal
                    onHide={() => setInspectOpen(false)}>
                {inspectLoading || !inspectData ? (
                    <div style={{ textAlign: 'center', padding: 40 }}>
                        <ProgressSpinner style={{ width: 40, height: 40 }} />
                        <p style={{ color: '#6b7280', marginTop: 8 }}>
                            Scanning chunks…
                        </p>
                    </div>
                ) : (
                    <div className="manage-db-inspect">
                        {/* Identity block — full chroma id, embedding model,
                            vector dimension. Helps diagnose dimension-mismatch
                            400s when an experiment script embeds with one
                            model but the collection was built with another. */}
                        <div style={{ background: '#f9fafb',
                                      border: '1px solid #e5e7eb',
                                      borderRadius: 6, padding: '10px 12px',
                                      marginBottom: 12, fontSize: 12.5 }}>
                            <div style={{ display: 'grid',
                                          gridTemplateColumns: '130px 1fr auto',
                                          rowGap: 4, columnGap: 12,
                                          alignItems: 'center' }}>
                                <span style={{ color: '#6b7280', fontWeight: 600 }}>Chroma ID</span>
                                <code style={{ fontFamily: 'ui-monospace, Menlo, monospace',
                                               fontSize: 12, color: '#374151',
                                               wordBreak: 'break-all' }}>
                                    {inspectTarget.id}
                                </code>
                                <button
                                    type="button"
                                    onClick={() => navigator.clipboard?.writeText(inspectTarget.id)
                                                     .then(() => toast.current?.show({
                                                         severity: 'success',
                                                         summary: 'Copied',
                                                         detail: 'Chroma ID copied to clipboard',
                                                         life: 1500 }))}
                                    title="Copy ID to clipboard"
                                    style={{ background: 'transparent', border: '1px solid #d1d5db',
                                             borderRadius: 4, padding: '2px 8px',
                                             fontSize: 11, color: '#374151',
                                             cursor: 'pointer' }}>
                                    📋 copy
                                </button>

                                <span style={{ color: '#6b7280', fontWeight: 600 }}>Embed model</span>
                                <code style={{ fontFamily: 'ui-monospace, Menlo, monospace',
                                               fontSize: 12,
                                               color: inspectTarget.embedModel
                                                      ? '#1d4ed8' : '#9ca3af' }}>
                                    {inspectTarget.embedModel || '(not recorded in collection metadata)'}
                                </code>
                                <span />

                                <span style={{ color: '#6b7280', fontWeight: 600 }}>Vector dim</span>
                                <code style={{ fontFamily: 'ui-monospace, Menlo, monospace',
                                               fontSize: 12,
                                               color: inspectData.vectorDim != null
                                                      ? '#15803d' : '#9ca3af' }}>
                                    {inspectData.vectorDim != null
                                        ? `${inspectData.vectorDim}-dim`
                                        : '(empty or unreadable)'}
                                </code>
                                <span />
                            </div>
                            {inspectData.vectorDim != null && inspectTarget.embedModel
                             && DIM_SANITY[inspectTarget.embedModel]
                             && DIM_SANITY[inspectTarget.embedModel] !== inspectData.vectorDim && (
                                <div style={{ marginTop: 8, color: '#b91c1c', fontSize: 12 }}>
                                    ⚠ Recorded model <code>{inspectTarget.embedModel}</code> is
                                    normally <strong>{DIM_SANITY[inspectTarget.embedModel]}</strong>-dim
                                    but this collection holds <strong>{inspectData.vectorDim}</strong>-dim
                                    vectors. The collection may have been re-tagged after a partial
                                    re-ingest — embed model and stored vectors disagree.
                                </div>
                            )}
                        </div>
                        <div className="manage-db-inspect-summary">
                            <div className="cmeta-stat">
                                <div className="cmeta-stat-num">
                                    {inspectTarget.count?.toLocaleString()}
                                </div>
                                <div className="cmeta-stat-lbl">total chunks</div>
                            </div>
                            <div className="cmeta-stat">
                                <div className="cmeta-stat-num">{inspectData.scanned}</div>
                                <div className="cmeta-stat-lbl">scanned for histograms</div>
                            </div>
                            <div className="cmeta-stat">
                                <div className="cmeta-stat-num">{inspectData.ingestRuns.length}</div>
                                <div className="cmeta-stat-lbl">distinct ingestion runs</div>
                            </div>
                        </div>
                        {inspectData.capped && (
                            <div style={{ background: '#fef3c7', color: '#92400e',
                                          padding: 10, borderRadius: 6, fontSize: 13,
                                          margin: '8px 0' }}>
                                ⚠ Histograms based on a 2 000-chunk sample;
                                full collection is {inspectTarget.count?.toLocaleString()}.
                            </div>
                        )}

                        <Divider />
                        <h4 style={{ margin: '0 0 8px' }}>Sections (top 10)</h4>
                        <Histogram entries={inspectData.sectionsTop}
                                   total={inspectData.scanned} />

                        <Divider />
                        <h4 style={{ margin: '0 0 8px' }}>Source files (top 10)</h4>
                        <Histogram entries={inspectData.sourcesTop}
                                   total={inspectData.scanned} />

                        {inspectData.ingestRuns.length > 0 && (
                            <>
                                <Divider />
                                <h4 style={{ margin: '0 0 8px' }}>Ingestion runs</h4>
                                <ul className="manage-db-runs">
                                    {inspectData.ingestRuns.map(r => (
                                        <li key={r}><code>{r}</code></li>
                                    ))}
                                </ul>
                            </>
                        )}

                        <Divider />
                        <h4 style={{ margin: '0 0 8px' }}>Sample chunk IDs</h4>
                        <ul className="manage-db-samples">
                            {inspectData.sampleChunks.map(c => (
                                <li key={c.id}>
                                    <code>{c.id}</code>
                                    {c.section && <Tag value={c.section}
                                                       severity="info"
                                                       style={{ marginLeft: 8, fontSize: 10 }} />}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </Dialog>
        </div>
    );
};

function Histogram({ entries, total }) {
    if (!entries.length)
        return <div style={{ color: '#9ca3af', fontStyle: 'italic' }}>—</div>;
    const max = Math.max(...entries.map(([, v]) => v));
    return (
        <div className="manage-db-hist">
            {entries.map(([k, v]) => {
                const pct = total ? (v / total) * 100 : 0;
                const widthPct = max ? (v / max) * 100 : 0;
                return (
                    <div className="manage-db-hist-row" key={k}>
                        <div className="manage-db-hist-label" title={k}>{k}</div>
                        <div className="manage-db-hist-bar">
                            <div className="manage-db-hist-fill"
                                 style={{ width: `${widthPct}%` }} />
                        </div>
                        <div className="manage-db-hist-count">
                            {v.toLocaleString()} <span style={{ color: '#9ca3af' }}>
                                ({pct.toFixed(1)}%)
                            </span>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export default ManageDB;
